import { Alert } from "@/src/lib/alert";
import { Link } from "expo-router";
import { sendPasswordResetEmail, signInWithEmailAndPassword } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { Keyboard, Platform, Pressable, StyleSheet, Text, type ScrollView, type TextInput, View } from "react-native";

import { BrandIntro } from "@/src/components/brand/BrandIntro";
import { AuthField } from "@/src/components/ui/AuthField";
import { PrimaryButton } from "@/src/components/ui/PrimaryButton";
import { ScreenContainer } from "@/src/components/ui/ScreenContainer";
import { auth } from "@/src/lib/firebase";
import { friendlyFirebaseError } from "@/src/lib/friendlyFirebaseError";
import { spacing, text, typography } from "@/src/theme/tokens";

export default function LoginScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const scrollViewRef = useRef<ScrollView>(null);
  const passwordRef = useRef<TextInput>(null);

  useEffect(() => {
    if (Platform.OS !== "android") return;

    const keyboardDidShow = Keyboard.addListener("keyboardDidShow", () => setKeyboardVisible(true));
    const keyboardDidHide = Keyboard.addListener("keyboardDidHide", () => setKeyboardVisible(false));

    return () => {
      keyboardDidShow.remove();
      keyboardDidHide.remove();
    };
  }, []);

  async function onLogin() {
    if (!email || !password) {
      Alert.alert("Erreur", "Email et mot de passe requis.");
      return;
    }
    try {
      setBusy(true);
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (error: any) {
      Alert.alert(
        "Connexion impossible",
        friendlyFirebaseError(error, "Impossible de se connecter. Réessaie.")
      );
    } finally {
      setBusy(false);
    }
  }

  async function onForgotPassword() {
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      Alert.alert("Mot de passe oublié", "Entre d’abord ton email pour recevoir le lien.");
      return;
    }
    try {
      setBusy(true);
      await sendPasswordResetEmail(auth, cleanEmail);
      Alert.alert("Email envoyé", "Un lien de réinitialisation vient d’être envoyé à ton adresse email.");
    } catch (error: any) {
      Alert.alert(
        "Impossible d’envoyer l’email",
        friendlyFirebaseError(error, "Impossible d’envoyer l’email. Réessaie.")
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <ScreenContainer
      contentStyle={Platform.OS === "android" && keyboardVisible ? styles.keyboardContent : styles.content}
      keyboardBehavior={Platform.OS === "android" ? "height" : undefined}
      scrollViewRef={scrollViewRef}
    >
      <View style={styles.intro}>
        <BrandIntro />
        <View style={styles.headingGroup}>
          <Text style={styles.heading}>Heureux de te revoir.</Text>
          <Text style={styles.body}>Connecte-toi pour retrouver ta communauté d’apprentissage.</Text>
        </View>
      </View>

      <View style={styles.form}>
        <AuthField
          autoCapitalize="none"
          autoComplete="email"
          autoCorrect={false}
          blurOnSubmit={false}
          keyboardType="email-address"
          label="Email"
          onChangeText={setEmail}
          onSubmitEditing={() => {
            setTimeout(() => passwordRef.current?.focus(), 100);
          }}
          placeholder="nom@exemple.com"
          returnKeyType="next"
          value={email}
        />
        <AuthField
          ref={passwordRef}
          autoCapitalize="none"
          autoComplete="current-password"
          autoCorrect={false}
          label="Mot de passe"
          onChangeText={setPassword}
          onFocus={() => {
            if (Platform.OS === "android") {
              const scrollView = scrollViewRef.current;
              const passwordField = passwordRef.current;
              if (scrollView && passwordField) {
                scrollView.scrollResponderScrollNativeHandleToKeyboard(passwordField, 24, true);
                setTimeout(() => scrollView.scrollToEnd({ animated: true }), 100);
              }
            }
          }}
          onSubmitEditing={() => void onLogin()}
          placeholder="Ton mot de passe"
          returnKeyType="done"
          secureTextEntry
          value={password}
        />
        <Pressable
          accessibilityRole="button"
          disabled={busy}
          hitSlop={8}
          onPress={() => void onForgotPassword()}
          style={styles.forgot}
        >
          <Text style={styles.link}>Mot de passe oublié ?</Text>
        </Pressable>
        <PrimaryButton busy={busy} busyLabel="Connexion…" label="Se connecter" onPress={() => void onLogin()} />
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerText}>Nouveau sur Neqraw ?</Text>
        <Link href="/(auth)/register" asChild>
          <Pressable accessibilityRole="link" hitSlop={8}>
            <Text style={styles.link}>Créer un compte</Text>
          </Pressable>
        </Link>
      </View>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  content: { justifyContent: "center", gap: spacing.xxl },
  keyboardContent: { justifyContent: "flex-start", gap: spacing.xl },
  intro: { gap: spacing.xl },
  headingGroup: { gap: spacing.sm },
  heading: { color: text.primary, fontFamily: typography.family.semibold, fontSize: typography.size.heading, lineHeight: typography.lineHeight.heading },
  body: { color: text.secondary, fontFamily: typography.family.regular, fontSize: typography.size.body, lineHeight: typography.lineHeight.body, maxWidth: 420 },
  form: { gap: spacing.lg },
  forgot: { alignSelf: "flex-end", minHeight: 44, justifyContent: "center" },
  link: { color: text.brand, fontFamily: typography.family.semibold, fontSize: 15, lineHeight: 22 },
  footer: { alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: spacing.sm, justifyContent: "center" },
  footerText: { color: text.secondary, fontFamily: typography.family.regular, fontSize: 15, lineHeight: 22 },
});
